import { scmpPlus, theme } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { Fragment, useEffect, useMemo } from "react";
import { graphql, usePaginationFragment, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { useTopBannerAdSlot } from "~/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import {
  TargetingHomePageTakeOverType,
  TargetingPaidType,
} from "~/components/advertisement/ad-slots/types";
import { FallbackLoading } from "~/components/fallback-loading";
import { PageSeo } from "~/components/meta";
import { trackingAtom } from "~/components/tracking";
import { data } from "~/data";
import { section as sectionData } from "~/data/section";
import { useInfiniteScrollTriggerReference } from "~/lib/hooks";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import type { livePageLatestContent$key } from "~/queries/__generated__/livePageLatestContent.graphql";
import type { livePageQuery } from "~/queries/__generated__/livePageQuery.graphql";

import { getDesktopAdUnit, getMobileAdUnit } from "./helpers";
import {
  AdContainer,
  Container,
  Heading,
  StyledAdSlot,
  StyledAppFooter,
  StyledContentItemLivePrimary,
  StyledLoading,
} from "./styles";

const query = graphql`
  query livePageQuery(
    $after: String
    $count: Int = 30
    $applicationIds: [String]
    $excludeArticleTypeIds: [String]
    $excludeSectionIds: [String]
    $scmpPlusPaywallTypeIds: [String]
  ) {
    ...livePageLatestContent
      @arguments(
        after: $after
        count: $count
        applicationIds: $applicationIds
        excludeArticleTypeIds: $excludeArticleTypeIds
        excludeSectionIds: $excludeSectionIds
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
      )
  }
`;

const livePageSize = 30;
const adZone = "homelive/home";

const LivePage: RelayComponent<unknown, livePageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);

  const {
    data: contentList,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<livePageQuery, livePageLatestContent$key>(
    graphql`
      fragment livePageLatestContent on Query
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 30 }
        applicationIds: { type: "[String]", defaultValue: [] }
        excludeArticleTypeIds: { type: "[String]", defaultValue: [] }
        excludeSectionIds: { type: "[String]", defaultValue: [] }
        scmpPlusPaywallTypeIds: { type: "[String]" }
      )
      @refetchable(queryName: "livePageLatestPaginationQuery") {
        contents(
          after: $after
          first: $count
          contentTypes: [ARTICLE, GALLERY, VIDEO]
          articleFilterInput: { sponsorTypes: [NONE] }
          articleExcludeInput: {
            articleTypeIds: $excludeArticleTypeIds
            paywallTypeIds: $scmpPlusPaywallTypeIds
          }
          filter: { applicationIds: $applicationIds }
          exclude: { sectionIds: $excludeSectionIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "livePage__article_contents") {
          edges {
            node {
              ...livePrimaryContentItemContent
            }
          }
        }
      }
    `,
    data,
  );

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(livePageSize),
  });

  useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner1",
      targeting: {
        hpto: TargetingHomePageTakeOverType.Top,
        paid: TargetingPaidType.Free,
      },
      zone: adZone,
    },
    mobile: {
      adUnit: "m_banner3",
      targeting: {
        hpto: TargetingHomePageTakeOverType.Top,
        paid: TargetingPaidType.Free,
      },
      zone: adZone,
    },
  });

  const isShowFooter = useMemo(
    () => !contentList.contents?.edges || !hasNextPage,
    [contentList.contents?.edges, hasNextPage],
  );

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  return (
    <Container>
      <PageSeo
        description="Your source for credible news and authoritative insights from Hong Kong, China and the world."
        openGraph={{
          images: [
            {
              url: "https://www.scmp.com/static/img/icons/scmp-meta-1200x630.png",
            },
          ],
        }}
        title="Latest News"
        twitter={{
          cardType: "summary",
        }}
      />
      <Heading>Latest</Heading>
      {contentList.contents.edges.map(({ node }, index) => (
        <Fragment key={index}>
          {index > 0 && index % 5 === 0 && (
            <AdContainer>
              <StyledAdSlot
                adUnit={getDesktopAdUnit(index / 5 - 1)}
                breakpoint={theme.breakpoints.up("tablet")}
                sizes={[[300, 250], "fluid"]}
                targeting={{
                  paid: TargetingPaidType.Free,
                }}
                zone={adZone}
                withBackground
                withLabel
                withSticky
              />
              <StyledAdSlot
                adUnit={getMobileAdUnit(index / 5 - 1)}
                breakpoint={theme.breakpoints.only("mobile")}
                sizes={[[300, 250], "fluid"]}
                targeting={{
                  paid: TargetingPaidType.Free,
                }}
                zone={adZone}
                withBackground
                withLabel
                withSticky
              />
            </AdContainer>
          )}
          <StyledContentItemLivePrimary reference={node} />
        </Fragment>
      ))}
      {hasNextPage && <StyledLoading ref={element => infiniteScrollTriggerReference(element)} />}
      {isShowFooter && <StyledAppFooter variant="home" />}
    </Container>
  );
};

LivePage.displayName = "LivePage";

const pageProps: PageProps = {
  appBarConfiguration: {
    desktopBreakpoint: "homeDesktop",
    hasDesktopAd: true,
    hasMobileAd: true,
    variant: "scmp/home-slim",
  },
  contentConfiguration: {
    responsivePadding: {
      tabletUp: 4,
    },
  },
};

export default withRelay(LivePage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext() {
    return {
      applicationIds: [null, data.application.scmp.entityUuid],
      excludeArticleTypeIds: [data.articleTypeEntityUuids.Letters],
      excludeSectionIds: [sectionData.yp.entityUuid, sectionData.posties.entityUuid],
      scmpPlusPaywallTypeIds: [scmpPlus?.article?.paywallTypeId],
    };
  },
});
